import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface CallingProvider {
  id: string;
  name: string;
  country_prefix: string;
  termination_uri: string;
  transport: string;
  sip_auth_username?: string | null;
  display_order: number;
  is_active: boolean;
}

interface CallingProviderModalProps {
  isAddMode: boolean;
  existingProvider: CallingProvider | null;
  onClose: () => void;
  onSave: (data: {
    name: string;
    country_prefix: string;
    termination_uri: string;
    transport: string;
    sip_auth_username?: string;
    sip_auth_password?: string;
    display_order: number;
    is_active: boolean;
  }) => Promise<void>;
}

const TRANSPORT_OPTIONS = ['UDP', 'TCP', 'TLS'];

export const CallingProviderModal: React.FC<CallingProviderModalProps> = ({
  isAddMode,
  existingProvider,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [countryPrefix, setCountryPrefix] = useState('');
  const [terminationUri, setTerminationUri] = useState('');
  const [transport, setTransport] = useState('UDP');
  const [sipAuthUsername, setSipAuthUsername] = useState('');
  const [sipAuthPassword, setSipAuthPassword] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existingProvider) {
      setName(existingProvider.name);
      setCountryPrefix(existingProvider.country_prefix);
      setTerminationUri(existingProvider.termination_uri);
      setTransport(existingProvider.transport || 'UDP');
      setSipAuthUsername(existingProvider.sip_auth_username || '');
      setSipAuthPassword('');
      setDisplayOrder(existingProvider.display_order ?? 0);
      setIsActive(existingProvider.is_active ?? true);
    } else {
      setName('');
      setCountryPrefix('');
      setTerminationUri('');
      setTransport('UDP');
      setSipAuthUsername('');
      setSipAuthPassword('');
      setDisplayOrder(0);
      setIsActive(true);
    }
  }, [existingProvider]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!countryPrefix.trim()) {
      setError('Country prefix is required (e.g. +91, +1)');
      return;
    }
    if (!terminationUri.trim()) {
      setError('Termination URI is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await onSave({
        name: name.trim(),
        country_prefix: countryPrefix.trim(),
        termination_uri: terminationUri.trim(),
        transport,
        sip_auth_username: sipAuthUsername.trim() || undefined,
        sip_auth_password: sipAuthPassword ? sipAuthPassword : undefined,
        display_order: displayOrder,
        is_active: isActive,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative z-10 w-full max-w-md rounded-lg bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {isAddMode ? 'Add Calling Provider' : 'Edit Calling Provider'}
            </h2>
            <button
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Indian, International"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Country Prefix (E.164)</label>
              <input
                type="text"
                value={countryPrefix}
                onChange={(e) => setCountryPrefix(e.target.value)}
                placeholder="e.g. +91, +1"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Termination URI</label>
              <input
                type="text"
                value={terminationUri}
                onChange={(e) => setTerminationUri(e.target.value)}
                placeholder="e.g. pbx.swsagent.com:5060"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Transport</label>
              <select
                value={transport}
                onChange={(e) => setTransport(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {TRANSPORT_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">SIP Auth Username (optional)</label>
              <input
                type="text"
                value={sipAuthUsername}
                onChange={(e) => setSipAuthUsername(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">SIP Auth Password (optional)</label>
              <input
                type="password"
                value={sipAuthPassword}
                onChange={(e) => setSipAuthPassword(e.target.value)}
                placeholder={isAddMode ? '' : 'Leave blank to keep existing'}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Display Order</label>
              <input
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : isAddMode ? 'Add' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
